/**
 * Classroom = Daily video call + tldraw collaborative whiteboard.
 *
 * - Resizable split via react-resizable-panels (drag the divider).
 * - Daily call embedded with @daily-co/daily-js iframe — owner (teacher) joins
 *   with `start_cloud_recording` token, so recording starts automatically when
 *   their plan supports it.
 * - tldraw uses its store listen API; we relay diffs through our backend
 *   WebSocket so both participants see edits in near real-time.
 */
import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { ResizablePanelGroup, ResizablePanel, ResizableHandle } from "../components/ui/resizable";
import VideoCall from "../components/VideoCall";
import Whiteboard from "../components/Whiteboard";
import ErrorBoundary from "../components/ErrorBoundary";
import ShareToStudentButton from "../components/ShareToStudentButton";

export default function Classroom() {
  const { lessonId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const session = location.state;
  const [leaving, setLeaving] = useState(false);

  // Without join state we cannot do anything — bounce back.
  useEffect(() => {
    if (!session || session.lesson_id !== lessonId) {
      navigate("/", { replace: true });
    }
  }, [session, lessonId, navigate]);

  if (!session || session.lesson_id !== lessonId) return null;

  const handleLeave = () => {
    setLeaving(true);
    if (session.role === "student") {
      navigate(`/classroom/${lessonId}/feedback`, { state: session, replace: true });
    } else {
      navigate("/", { replace: true });
    }
  };

  return (
    <main
      className="fixed inset-0 bg-[#0E0F12] flex flex-col"
      data-testid="classroom-page"
    >
      {/* Slim top bar */}
      <div className="flex items-center justify-between px-4 md:px-6 h-12 border-b border-white/5 text-white shrink-0">
        <div className="flex items-center gap-3 min-w-0">
          <span className="text-[10px] tracking-[0.32em] uppercase opacity-60 shrink-0">
            {session.role === "teacher" ? "Teacher" : "Student"}
          </span>
          <span className="font-display text-base md:text-lg truncate">
            {session.title}
          </span>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          {session.role === "teacher" && (
            <ShareToStudentButton lessonId={lessonId} />
          )}
          <button
            data-testid="classroom-leave-btn"
            onClick={handleLeave}
            disabled={leaving}
            className="text-[11px] tracking-[0.28em] uppercase font-medium px-3 py-1.5 rounded-md hover:bg-white/10 transition-colors disabled:opacity-40"
          >
            Leave →
          </button>
        </div>
      </div>

      {/* Split body */}
      <div className="flex-1 min-h-0">
        <ResizablePanelGroup
          direction="horizontal"
          className="h-full w-full"
          autoSaveId={`classroom-split-${session.role}`}
        >
          <ResizablePanel defaultSize={50} minSize={25} className="bg-black">
            <ErrorBoundary>
              <VideoCall
                roomUrl={session.daily_room_url}
                token={session.daily_token}
                userName={session.role === "teacher" ? "Teacher" : "Student"}
                onLeft={handleLeave}
              />
            </ErrorBoundary>
          </ResizablePanel>
          <ResizableHandle withHandle className="bg-white/5 hover:bg-white/10 transition-colors" />
          <ResizablePanel defaultSize={50} minSize={25} className="bg-white">
            <ErrorBoundary>
              <Whiteboard
                lessonId={lessonId}
                wsToken={session.whiteboard_token}
                role={session.role}
              />
            </ErrorBoundary>
          </ResizablePanel>
        </ResizablePanelGroup>
      </div>
    </main>
  );
}
